import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/dashboard/widgets";
import { ShieldCheck, FileDown, Loader2, BarChart3, Activity, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

/**
 * /dashboard/compliance — buyer-side FCA audit pack + sub-id A/B analytics (iter 59).
 *
 *  - Summary cards: 30d / 90d / 365d TrustedForm coverage %
 *  - Bulk CSV export with full consent chain
 *  - Per-source_sub_id accept-rate + spend table
 */
export default function Compliance() {
  const [summary, setSummary] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, src] = await Promise.all([
          api.get("/client/compliance/summary"),
          api.get("/client/sources/analytics?days=30"),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setSources(src.data);
      } catch {
        if (!cancelled) toast.error("Failed to load compliance data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const token = localStorage.getItem("connexis_token");
      const resp = await fetch(`${BACKEND_URL}/api/client/compliance/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `connexis_compliance_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit pack downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="size-5 animate-spin mr-2" />Loading…</div>;
  }

  const cards = [
    { label: "Last 30 days",  key: "d30",  data: summary?.d30 },
    { label: "Last 90 days",  key: "d90",  data: summary?.d90 },
    { label: "Last 365 days", key: "d365", data: summary?.d365 },
  ];

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <SectionTitle
        title="Compliance & audit"
        subtitle="FCA-audit-ready trail of every lead — TrustedForm coverage, full consent chain, IP / UA capture, and per-source A/B analytics."
      />

      {/* TrustedForm coverage cards */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="compliance-summary">
        {cards.map((c) => (
          <Card key={c.key} className="rounded-xl border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                <ShieldCheck className="size-3.5" /> {c.label}
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="text-3xl font-semibold tabular-nums text-slate-900">
                  {(c.data?.coverage_pct ?? 0).toFixed(1)}%
                </div>
                <div className="text-[12px] text-slate-500">consent coverage</div>
              </div>
              <div className="mt-2 text-[12px] text-slate-600 tabular-nums">
                {c.data?.consented?.toLocaleString() ?? 0} / {c.data?.total?.toLocaleString() ?? 0} leads consented
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${(c.data?.coverage_pct ?? 0) >= 95 ? "bg-emerald-500" : (c.data?.coverage_pct ?? 0) >= 80 ? "bg-amber-500" : "bg-rose-500"}`}
                  style={{ width: `${Math.min(100, c.data?.coverage_pct ?? 0)}%` }}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CSV export */}
      <Card className="rounded-xl border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <FileDown className="size-5 text-[#0055FF] mt-1" strokeWidth={1.6} />
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-slate-900">Bulk audit pack export</div>
              <div className="text-[12.5px] text-slate-500">
                Every lead with TrustedForm URL, consent token, timestamp, IP, user-agent and disposition — CSV format, FCA-ready.
              </div>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 max-w-2xl">
            <div>
              <Label className="text-[12px] text-slate-700">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="compliance-from" />
            </div>
            <div>
              <Label className="text-[12px] text-slate-700">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="compliance-to" />
            </div>
            <div className="flex items-end">
              <Button onClick={exportCsv} disabled={exporting} className="rounded-full w-full" data-testid="compliance-export-btn">
                {exporting ? <><Loader2 className="size-3.5 animate-spin mr-1" /> Generating…</> : <><FileDown className="size-3.5 mr-1" /> Download CSV</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-id analytics */}
      <Card className="rounded-xl border-slate-200">
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <BarChart3 className="size-5 text-[#0055FF]" strokeWidth={1.6} />
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-slate-900">Source A/B analytics</div>
              <div className="text-[12.5px] text-slate-500">Accept-rate, returns and sold conversions per <span className="font-mono">source_sub_id</span> (last 30 days).</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sources-table">
              <thead className="bg-slate-50/60 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                  <th className="py-3 px-6 font-medium">Source</th>
                  <th className="font-medium text-right">Total</th>
                  <th className="font-medium text-right">Accepted</th>
                  <th className="font-medium text-right">Returned</th>
                  <th className="font-medium text-right">Sold</th>
                  <th className="font-medium text-right">Accept rate</th>
                  <th className="font-medium text-right pr-6">Spend</th>
                </tr>
              </thead>
              <tbody>
                {(sources?.rows || []).length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-[12.5px] text-slate-500">
                    <Activity className="size-4 mx-auto mb-2 opacity-40" />
                    No leads in the last 30 days. Publisher sub-ids will appear here as leads arrive.
                  </td></tr>
                )}
                {(sources?.rows || []).map((r, i) => (
                  <tr key={`${r.publisher_id || "n"}|${r.source_sub_id}`} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`source-row-${i}`}>
                    <td className="py-3.5 px-6 font-mono text-[12px] text-slate-800">{r.source_sub_id}</td>
                    <td className="py-3.5 text-right tabular-nums text-slate-700">{r.total}</td>
                    <td className="py-3.5 text-right tabular-nums text-slate-700">{r.accepted}</td>
                    <td className="py-3.5 text-right tabular-nums text-slate-700">{r.returned}</td>
                    <td className="py-3.5 text-right tabular-nums text-emerald-700 font-medium">{r.sold}</td>
                    <td className="py-3.5 text-right tabular-nums">
                      <span className={`text-[12px] px-2 py-0.5 rounded-full ${r.accept_rate >= 0.7 ? "bg-emerald-50 text-emerald-700" : r.accept_rate >= 0.4 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                        {(r.accept_rate * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3.5 pr-6 text-right tabular-nums text-slate-900 font-medium">£{r.spend_gbp.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Postback how-to */}
      <Card className="rounded-xl border-slate-200 bg-slate-900 text-slate-100">
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <Activity className="size-4 text-[#60A5FA]" strokeWidth={1.6} />
            <div className="text-[12px] font-semibold uppercase tracking-widest text-slate-300">CRM postback (closes the loop)</div>
          </div>
          <p className="mt-3 text-[13px] text-slate-300">When your CRM marks a lead as sold/junk, POST it back to us so we tune the ping-tree against your real outcomes:</p>
          <pre className="mt-3 text-[11.5px] font-mono whitespace-pre overflow-x-auto bg-black/40 rounded-lg p-4 leading-relaxed">{`curl -X POST https://connex.is/api/v1/leads/{lead_id}/postback \\
  -H "X-Postback-Token: <token from delivered lead>" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"sold","sale_value_gbp":1200,"notes":"closed at 4.6%"}'`}</pre>
          <a href="/docs" className="mt-4 inline-flex items-center gap-1 text-[12.5px] text-[#60A5FA] hover:underline">
            Full postback docs <ArrowUpRight className="size-3" />
          </a>
        </CardContent>
      </Card>
    </div>
  );
}
